import { Button } from '@/components/atoms/Button';
import { Card } from '@/components/atoms/Card';
import { GlobeIcon } from '@/components/atoms/GlobeIcon';
import { HandHeartIcon } from '@/components/atoms/HandHeartIcon';
import { HouseIcon } from '@/components/atoms/HouseIcon';
import { Overlay } from '@/components/atoms/Overlay';
import { Typography } from '@/components/atoms/Typography';
import { UAEPassIcon } from '@/components/atoms/UAEPassIcon';
import { HeadsetIcon } from '@/components/atoms/icons/HeadsetIcon';
import { LogoHeader } from '@/components/molecules/header/LogoHeader';
import { ENABLE_UAEPASS } from '@/core/constants/appConfig';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useI18nContext, useTranslation, useUIColors } from '@/core/hooks';
import { FlatTranslationKey } from '@/core/types/i18n';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Animated, StyleSheet, View } from 'react-native';
import { WelcomeBackground } from './WelcomeBackground';

// Registration flow states
export enum RegistrationState {
    INITIAL = 'initial',
    LOGIN = 'login',
    UAE_OPTIONS = 'uae_options',
    UAE_METHODS = 'uae_methods',
    INTERNATIONAL = 'international'
}

export interface WelcomeScreenProps {
    currentState?: RegistrationState;
    onRegisterPress?: (() => void) | undefined;
    onLoginPress?: (() => void) | undefined;
    onLoginWithEmailPress?: (() => void) | undefined;
    onLoginWithUAEPassPress?: (() => void) | undefined;
    onLanguagePress?: (() => void) | undefined;
    onUAECitizenPress?: (() => void) | undefined;
    onInternationalPress?: (() => void) | undefined;
    onUAEPassPress?: (() => void) | undefined;
    onEmiratesIdPress?: (() => void) | undefined;
    onPassportPress?: (() => void) | undefined;
    onBackPress?: (() => void) | undefined;
}

export const WelcomeScreen = React.memo<WelcomeScreenProps>(function WelcomeScreen({
    currentState = RegistrationState.INITIAL,
    onRegisterPress,
    onLoginPress,
    onLoginWithEmailPress,
    onLoginWithUAEPassPress,
    onLanguagePress,
    onUAECitizenPress,
    onInternationalPress,
    onUAEPassPress,
    onEmiratesIdPress,
    onPassportPress,
    onBackPress,
}) {
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const colors = useUIColors();

    useCatalogDynatraceView(DT_SCREEN.WELCOME, 'welcome_page_viewed', true);

    const handleLoginPress = React.useCallback(() => {
        trackCatalogEvent({
            screen: DT_SCREEN.WELCOME,
            actionName: 'welcome_login_tap',
            eventType: 'action',
        });
        onLoginPress?.();
    }, [onLoginPress]);

    const fadeAnim = React.useRef(new Animated.Value(1)).current;
    const slideAnim = React.useRef(new Animated.Value(0)).current;
    const [buttonScaleAnim] = useState(new Animated.Value(1));
    const [isAnimating, setIsAnimating] = useState(false);

    // Reset animation values when state changes externally (e.g., from URL params)
    // This ensures smooth appearance when navigating directly to a state
    React.useEffect(() => {
        if (!isAnimating) {
            fadeAnim.setValue(1);
            slideAnim.setValue(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentState]);

    const animateTransition = (callback: () => void) => {
        if (isAnimating) return; // Prevent animation interruption

        setIsAnimating(true);

        // Fade out and slide
        Animated.parallel([
            Animated.timing(fadeAnim, {
                toValue: 0,
                duration: 200,
                useNativeDriver: true,
            }),
            Animated.timing(slideAnim, {
                toValue: isRTL ? -30 : 30,
                duration: 200,
                useNativeDriver: true,
            })
        ]).start(() => {
            // Update state after fade-out completes
            callback();

            // Reset slide position from opposite direction
            slideAnim.setValue(isRTL ? 30 : -30);

            // Fade in with slide
            Animated.parallel([
                Animated.timing(fadeAnim, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true,
                }),
                Animated.timing(slideAnim, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                })
            ]).start(() => {
                setIsAnimating(false);
            });
        });
    };

    const animateButtonPress = () => {
        Animated.sequence([
            Animated.timing(buttonScaleAnim, {
                toValue: 0.95,
                duration: 80,
                useNativeDriver: true,
            }),
            Animated.timing(buttonScaleAnim, {
                toValue: 1,
                duration: 80,
                useNativeDriver: true,
            })
        ]).start();
    };

    const handleRegisterPress = () => {
        animateButtonPress();
        animateTransition(() => {
            onRegisterPress?.();
        });
    };

    const handleUAECitizenPress = () => {
        if (!ENABLE_UAEPASS) {
            // UAE Pass disabled: directly call callback without animation
            onUAECitizenPress?.();
        } else {
            // UAE Pass enabled: use animation transition
            animateTransition(() => {
                onUAECitizenPress?.();
            });
        }
    };

    const handleInternationalPress = () => {
        if (!ENABLE_UAEPASS) {
            // UAE Pass disabled: directly call callback without animation
            onInternationalPress?.();
        } else {
            // UAE Pass enabled: use animation transition
            animateTransition(() => {
                onInternationalPress?.();
            });
        }
    };

    const handleBackPress = () => {
        animateTransition(() => {
            onBackPress?.();
        });
    };

    const getStateContent = () => {
        switch (currentState) {
            case RegistrationState.INITIAL:
                return {
                    miniTitle: t('welcome.welcomeTo'),
                    title: t('welcome.appName'),
                    subtitle: `${t('welcome.subtitle1')}`
                };
            case RegistrationState.LOGIN:
                return {
                    miniTitle: '',
                    title: t('auth.welcomeBack'),
                    subtitle: t('auth.loginToAccessProperties')
                };
            case RegistrationState.UAE_OPTIONS:
                return {
                    miniTitle: '',
                    title: t('welcome.registerTitle'),
                    subtitle: t('welcome.registerSubtitle')
                };
            case RegistrationState.UAE_METHODS:
                return {
                    miniTitle: '',
                    title: t('welcome.uaeMethodsTitle'),
                    subtitle: t('welcome.uaeMethodsSubtitle')
                };
            case RegistrationState.INTERNATIONAL:
                return {
                    miniTitle: '',
                    title: t('welcome.internationalMethodsTitle'),
                    subtitle: t('welcome.internationalMethodsSubtitle')
                };
            default:
                return {
                    miniTitle: t('welcome.welcomeTo'),
                    title: t('welcome.appName'),
                    subtitle: `${t('welcome.subtitle1')}\n\n${t('welcome.subtitle2')}`
                };
        }
    };

    const renderFAQButton = () => (
        <Button
            title={t('welcome.faqSupport' as FlatTranslationKey)}
            variant="text"
            theme="dark"
            fullWidth
            leading={<HeadsetIcon size={18} color="white" />}
            onPress={() => {
                const isAdha = currentState === RegistrationState.UAE_METHODS || currentState === RegistrationState.UAE_OPTIONS;
                router.push({
                    pathname: '/faq',
                    params: isAdha ? { isAdhaFlow: 'true' } : undefined
                } as any);
            }}
        />
    );

    const renderOrDivider = () => (
        <View style={styles.orDivider}>
            <View style={[styles.orDividerLine, { backgroundColor: colors.backgroundLevel4 }]} />
            <Typography variant="caption" color="white" style={styles.orText}>
                {t('welcome.or' as FlatTranslationKey)}
            </Typography>
            <View style={[styles.orDividerLine, { backgroundColor: colors.backgroundLevel4 }]} />
        </View>
    );

    const renderButtons = () => {
        switch (currentState) {
            case RegistrationState.INITIAL:
                return (
                    <>
                        <Button
                            title={t('auth.register')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            onPress={handleRegisterPress}
                        />
                        <Button
                            title={t('auth.login')}
                            variant="outline"
                            theme="dark"
                            fullWidth
                            onPress={handleLoginPress}
                        />
                        {renderFAQButton()}
                    </>
                );

            case RegistrationState.LOGIN:
                return (
                    <>
                        <Button
                            title={t('auth.loginWithUAEPass')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            leading={<UAEPassIcon size={18} />}
                            onPress={onLoginWithUAEPassPress}
                        />
                        <Button
                            title={t('auth.loginWithEmail')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            onPress={onLoginWithEmailPress}
                        />
                        {renderFAQButton()}
                    </>
                );

            case RegistrationState.UAE_OPTIONS:
                return (
                    <>
                        <Button
                            title={t('welcome.uaeCitizenResident')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            leading={<HouseIcon size={18} />}
                            onPress={handleUAECitizenPress}
                        />
                        <Button
                            title={t('welcome.internationalUser')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            leading={<GlobeIcon size={18} />}
                            onPress={handleInternationalPress}
                        />
                        {renderFAQButton()}
                    </>
                );

            case RegistrationState.UAE_METHODS:
                return (
                    <>
                        <Button
                            title={t('welcome.registerWithUaePass')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            leading={<UAEPassIcon size={18} />}
                            onPress={onUAEPassPress}
                        />
                        {renderOrDivider()}
                        <Button
                            title={t('welcome.registerWithEmiratesId')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            onPress={onEmiratesIdPress}
                        />
                        {renderFAQButton()}
                    </>
                );

            case RegistrationState.INTERNATIONAL:
                return (
                    <>
                        <Button
                            title={t('welcome.registerWithPassport')}
                            variant="primary"
                            theme="dark"
                            fullWidth
                            onPress={onPassportPress}
                        />
                        {renderFAQButton()}
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <View style={styles.container}>
            <WelcomeBackground>
                {/* Dark Overlay */}
                <Overlay type="dark" opacity={0.5} />


                {/* Header */}
                <LogoHeader
                    showBackButton={currentState !== RegistrationState.INITIAL}
                    onBackPress={handleBackPress}
                    onLanguagePress={onLanguagePress}
                    logoColor='white'
                    backButtonColor='white'
                    languageTextColor='white'
                />

                {/* Content Container */}
                <View style={styles.content}>
                    {/* Welcome Card positioned at bottom */}
                    <View style={styles.cardContainer}>
                        <Card
                            variant="transparent"
                            padding={12}
                            style={styles.card}
                        >
                            {/* Welcome Text Section */}
                            <Animated.View style={[
                                styles.textSection,
                                { direction: isRTL ? 'rtl' : 'ltr' },
                                { opacity: fadeAnim, transform: [{ translateX: slideAnim }] }
                            ]}>
                                {currentState === RegistrationState.LOGIN && (
                                    <View style={styles.iconSection}>
                                        <HandHeartIcon size={40} color="white" />
                                    </View>
                                )}
                                <View style={styles.titleSection}>
                                    {Boolean(getStateContent().miniTitle) && (
                                        <Typography
                                            variant="subhead"
                                            color="white"
                                            style={styles.miniTitle}
                                        >
                                            {getStateContent().miniTitle}
                                        </Typography>
                                    )}
                                    <Typography
                                        variant="title1"
                                        color="white"
                                        style={styles.mainTitle}
                                    >
                                        {getStateContent().title}
                                    </Typography>
                                </View>

                                <Typography
                                    variant="body"
                                    color="white"
                                    style={styles.subtitle}
                                >
                                    {getStateContent().subtitle}
                                </Typography>
                            </Animated.View>

                            {/* Buttons Section */}
                            <Animated.View style={[
                                styles.buttonSection,
                                { opacity: fadeAnim }
                            ]}>
                                {renderButtons()}
                            </Animated.View>
                        </Card>
                    </View>
                </View>
            </WelcomeBackground>
        </View>
    );
});

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    backgroundVideo: {
        flex: 1,
    },
    backgroundImage: {
        flex: 1,
    },
    content: {
        flex: 1,
        justifyContent: 'flex-end',
    },
    cardContainer: {
        paddingBottom: 37, // Adjusted space from bottom
        paddingHorizontal: 24,
        alignItems: 'center',
    },
    card: {
        width: 345, // Fixed width from Figma
    },
    textSection: {
        marginBottom: 48, // Gap between text and buttons from Figma
        alignItems: 'center',
    },
    iconSection: {
        marginBottom: 16, // Gap between icon and title
        alignItems: 'center',
    },
    titleSection: {
        marginBottom: 32, // Gap between title and subtitle from Figma
        alignItems: 'center',
    },
    miniTitle: {
        marginBottom: 8,
        textAlign: 'center',
        lineHeight: 18,
        fontWeight: '500',
        fontSize: 20,
        alignSelf: 'stretch',
    },
    mainTitle: {
        textAlign: 'center',
        lineHeight: 40,
        fontWeight: '500',
        fontSize: 28
    },
    subtitle: {
        textAlign: 'center',
        lineHeight: 24,
        fontWeight: '400',
        fontSize: 16,
    },
    buttonSection: {
        gap: 12, // Gap between buttons from Figma
    },
    orDivider: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        gap: 7,
        paddingVertical: 8,
    },
    orDividerLine: {
        flex: 1,
        height: 2,
        borderRadius: 1,
    },
    orText: {
        fontSize: 12,
        paddingHorizontal: 4,
    },
});

export default WelcomeScreen;
